// Upstream: shadcn-ui/ui apps/v4/registry/new-york-v4/ui/alert-dialog.tsx — MIT. Zoom/slide entrance removed; 150ms opacity fade via @starting-style.

import type { ComponentProps } from "react";
import { AlertDialog as AlertDialogPrimitive } from "radix-ui";
import { panelClass } from "@ahcut/ui/recipes";
import { Button, type ButtonProps } from "@/components/ui/button";
import { cn } from "@/lib/cn";

const fade = "transition-opacity duration-150 starting:opacity-0";

export const AlertDialog = AlertDialogPrimitive.Root;
export const AlertDialogPortal = AlertDialogPrimitive.Portal;

// Overlay and content set data-slot after the spread: print rules in
// styles.css hide both by slot.
export function AlertDialogOverlay({
  className,
  ...rest
}: ComponentProps<typeof AlertDialogPrimitive.Overlay>) {
  return (
    <AlertDialogPrimitive.Overlay
      {...rest}
      data-slot="alert-dialog-overlay"
      className={cn("fixed inset-0 z-50 bg-scrim", fade, className)}
    />
  );
}

export interface AlertDialogContentProps extends ComponentProps<typeof AlertDialogPrimitive.Content> {
  /** Backdrop click handler. Radix AlertDialog never dismisses on an outside
   *  click by itself, so callers that want click-to-cancel pass it here. */
  onOverlayClick?: () => void;
}

export function AlertDialogContent({ className, onOverlayClick, ...rest }: AlertDialogContentProps) {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay onClick={onOverlayClick} />
      <AlertDialogPrimitive.Content
        {...rest}
        data-slot="alert-dialog-content"
        className={cn(
          panelClass(),
          "fixed top-1/2 left-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-md",
          "-translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-y-auto shadow-overlay",
          fade,
          className,
        )}
      />
    </AlertDialogPortal>
  );
}

export function AlertDialogFooter({ className, ...rest }: ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn("flex flex-wrap items-center justify-end gap-3", className)}
      {...rest}
    />
  );
}

export function AlertDialogTitle({ className, ...rest }: ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn("text-lg font-semibold text-fg-0", className)}
      {...rest}
    />
  );
}

export function AlertDialogDescription({
  className,
  ...rest
}: ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn("text-sm text-fg-1", className)}
      {...rest}
    />
  );
}

// Cancel renders through <Button> (Radix asChild), so dialog buttons share the
// one class-composition path — variants, sizes and the link-colour fix
// included. There is no Action wrapper: ConfirmDialog confirms with a plain
// <Button>, because an async confirm must keep the dialog open while it runs.

/** Closes the dialog; receives initial focus when the dialog opens (Radix AlertDialog). */
export function AlertDialogCancel({ variant = "quiet", ...rest }: ButtonProps) {
  return (
    <AlertDialogPrimitive.Cancel asChild>
      <Button variant={variant} {...rest} />
    </AlertDialogPrimitive.Cancel>
  );
}
